import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiUpload, FiPlus, FiImage, FiChevronRight, FiX } from 'react-icons/fi';
import { apiFetch } from '../utils/api';
import { showToast } from './NotificationTray';
import PageHero from './PageHero';
import ArchiveFolderChildren from './ArchiveFolderChildren';
import BatchUploader from './SharedUploadSession';
import { useUpload } from '../contexts/UploadContext';
import PhotoPickerModal from './PhotoPickerModal';
import { buildVariantUrl } from '../utils/photoCache';
import useArchiveFolderImages from '../hooks/useArchiveFolderImages';
import { isProcessingLike, isTerminalProcessingFailure } from '../utils/processingState';

// A custom folder inside a Scan Archive month, the only place in the archive
// where photos live. Upload new scans directly, or move existing library
// photos in. Photos stay visible in the main library either way.
export default function ScanArchiveFolder() {
  const { folderId } = useParams();
  const { data, loading, error, unavailable, load } = useArchiveFolderImages(folderId);
  const [showUploader, setShowUploader] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const uploadContext = useUpload();



  const addFromLibrary = async (imageIds) => {
    const ids = Array.isArray(imageIds) ? imageIds : [imageIds];
    const res = await apiFetch(`/api/archive/folders/${folderId}/images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_ids: ids }),
    });
    const body = await res.json();
    if (!res.ok) {
      showToast(body.error || 'Could not move photos', 'error');
      return;
    }
    showToast(`Moved ${body.moved} photo${body.moved === 1 ? '' : 's'} into the folder`, 'success');
    setShowPicker(false);
    load();
  };

  const removeFromFolder = async (imageId) => {
    const res = await apiFetch(`/api/archive/folders/${folderId}/images`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_ids: [imageId] }),
    });
    if (res.ok) {
      showToast('Removed from folder (still in your library)', 'success');
      load();
    }
  };

  if (error && !data) return <div className="mobile-content-wrapper" role="alert">
    <p>{error}</p>
    {unavailable ? <Link to="/archive">Back to Scan Archive</Link> : <button onClick={load}>Try again</button>}
  </div>;
  if (loading || !data) {
    return (
      <div className="mobile-content-wrapper">
        <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  const { folder, month, year, images, folders = [], ancestors = [year, month].filter(Boolean) } = data;

  return (
    <div className="mobile-content-wrapper">
      <PageHero eyebrow="Scan Archive" title={folder.name}>
        <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
          <Link to="/archive" className="hover:text-blue-600">Archive</Link>
          {ancestors.map(row => <React.Fragment key={row.id}>
            <FiChevronRight size={14} />
            <Link to={`/archive/${row.kind === 'custom' ? 'folder' : row.kind}/${row.id}`} className="hover:text-blue-600">{row.name}</Link>
          </React.Fragment>)}
          <FiChevronRight size={14} />
          <span className="text-gray-800 font-medium">{folder.name}</span>
        </div>
      </PageHero>

      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() => { uploadContext?.maximizePanel(); setShowUploader(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
        >
          <FiUpload size={16} /> Upload scans
        </button>
        <button
          onClick={() => setShowPicker(true)}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <FiPlus size={16} /> Move from library
        </button>
      </div>

      <ArchiveFolderChildren key={folder.id} parent={folder} month={month} folders={folders} onChange={load} />

      {images.length === 0 ? (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <FiImage size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="font-semibold text-gray-800 mb-2">Nothing filed here yet</h3>
          <p className="text-sm text-gray-500">
            Upload scans straight into this folder, or move photos over from your library.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 mt-6">
          {images.map((img) => (
            <div key={img.id} className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
              {buildVariantUrl(img, 'sm') ? (
                <img
                  src={buildVariantUrl(img, 'sm')}
                  alt={img.title || img.original_filename || ''}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <FiImage size={20} />
                </div>
              )}
              {(isProcessingLike(img) || isTerminalProcessingFailure(img)) && (
                <span role="status" className="absolute bottom-0 inset-x-0 bg-white/90 text-xs p-1">
                  {isTerminalProcessingFailure(img) ? 'Preview needs attention' : 'Uploaded · preparing preview'}
                </span>
              )}
              <button
                onClick={() => removeFromFolder(img.id)}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove from folder (stays in library)"
              >
                <FiX size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showUploader && <BatchUploader archiveFolderId={folderId}
        onUploadComplete={() => load()}
        onRequestClose={() => { setShowUploader(false); load(); }}/>}

      {showPicker && (
        <PhotoPickerModal
          isOpen={true}
          onClose={() => setShowPicker(false)}
          onSelect={addFromLibrary}
          excludeIds={images.map((i) => i.id)}
          title={`Move photos into ${folder.name}`}
          multiSelect={true}
        />
      )}
    </div>
  );
}
